import { useState } from 'react';
import { Apontamento, deleteApontamento } from '../../services/apontamentoService';
import { Edit, Trash2, Loader2, Clock, User, Paperclip, Eye, Download } from 'lucide-react';
import { ApontamentoForm } from './ApontamentoForm';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { attachmentService } from '../../services/attachmentService';

interface ApontamentoListProps {
  apontamentos: Apontamento[];
  onUpdate: () => void;
  onDelete: () => void;
}

export const ApontamentoList = ({
  apontamentos,
  onUpdate,
  onDelete,
}: ApontamentoListProps) => {
  const [editingApontamento, setEditingApontamento] = useState<Apontamento | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(null);

  const handleDelete = async (id: string) => {
    try {
      setDeletingId(id);
      await deleteApontamento(id);
      setShowDeleteConfirm(null);
      onDelete();
    } catch (err: any) {
      console.error('Erro ao excluir apontamento:', err);
      alert(err.response?.data?.error || 'Erro ao excluir apontamento');
    } finally {
      setDeletingId(null);
    }
  };

  const getTipoHoraColor = (tipoHora: string) => {
    switch (tipoHora) {
      case 'HTC':
        return 'border-transparent text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30';
      case 'HTE':
        return 'border-transparent text-orange-600 dark:text-orange-400 bg-orange-50 dark:bg-orange-900/30';
      case 'PROJETO':
        return 'border-transparent text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/30';
      default:
        return 'border-transparent text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-800';
    }
  };

  const formatDate = (dateString: string) => {
    // Extrai apenas YYYY-MM-DD da string ISO, ignora timezone
    const datePart = dateString.substring(0, 10);
    const [year, month, day] = datePart.split('-');
    return `${day}/${month}/${year}`;
  };

  const formatTime = (dateString: string) => {
    // Extrai apenas HH:mm da string ISO, ignora timezone
    const timePart = dateString.split('T')[1];
    return timePart ? timePart.substring(0, 5) : '';
  };

  const formatMinutes = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours === 0) return `${mins}min`;
    if (mins === 0) return `${hours}h`;
    return `${hours}h ${mins}min`;
  };

  const formatCurrency = (value: string | number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(Number(value));
  };

  // Calcular totalizações
  const totalizacoes = apontamentos.reduce(
    (acc, apontamento) => {
      const tipo = apontamento.tipoHora;
      const horas = apontamento.minutosTotais / 60;
      const valor = (apontamento.minutosTotais / 60) * Number(apontamento.valorHora);

      if (!acc[tipo]) {
        acc[tipo] = { horas: 0, valor: 0 };
      }

      acc[tipo].horas += horas;
      acc[tipo].valor += valor;

      return acc;
    },
    {} as Record<string, { horas: number; valor: number }>
  );

  const valorTotalFaturavel = Object.entries(totalizacoes)
    .filter(([tipo]) => tipo !== 'PROJETO')
    .reduce((acc, [, data]) => acc + data.valor, 0);

  if (apontamentos.length === 0) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        <Clock className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
        <p>Nenhum apontamento registrado</p>
        <p className="text-sm mt-2">Clique em "Novo Apontamento" para começar</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Lista de Apontamentos */}
      <div className="space-y-3">
        {apontamentos.map((apontamento) => (
          <div
            key={apontamento.id}
            className={`border rounded-lg p-4 ${
              apontamento.faturado
                ? 'bg-muted border-border'
                : 'bg-card border-border hover:border-brand/50'
            } transition-colors`}
          >
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 space-y-2">
                {/* Header */}
                <div className="flex items-center gap-3 flex-wrap">
                  <span className="text-sm font-medium text-foreground">
                    {formatDate(apontamento.data)}
                  </span>
                  <span className="text-sm text-muted-foreground">
                    {formatTime(apontamento.horaInicio)} - {formatTime(apontamento.horaFim)}
                  </span>
                  <Badge variant="outline" className={getTipoHoraColor(apontamento.tipoHora)}>
                    {apontamento.tipoHora}
                  </Badge>
                  <span className="text-sm font-medium text-foreground">
                    {formatMinutes(apontamento.minutosTotais)}
                  </span>
                  {apontamento.faturado && (
                    <Badge variant="secondary" className="border-transparent bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-400">
                      FATURADO
                    </Badge>
                  )}
                </div>

                {/* Técnico */}
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <User className="w-4 h-4" />
                  {apontamento.user.name}
                </div>

                {/* Descrição */}
                <p className="text-sm text-foreground whitespace-pre-wrap">
                  {apontamento.descricao}
                </p>

                {/* Anexos */}
                {apontamento.anexos && apontamento.anexos.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {apontamento.anexos.map((anexo) => (
                      <span key={anexo.id} className="inline-flex items-center gap-2">
                        <span className="inline-flex items-center gap-1.5 px-2 py-1 bg-muted rounded-md text-xs text-muted-foreground">
                          <Paperclip className="w-3 h-3" />
                          {anexo.filename}
                        </span>
                        <button
                          onClick={() => attachmentService.view(apontamento.ticketId, anexo.id)}
                          title="Visualizar"
                          className="p-0.5 hover:text-brand transition-colors"
                        >
                          <Eye className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => attachmentService.download(apontamento.ticketId, anexo.id)}
                          title="Baixar"
                          className="p-0.5 hover:text-brand transition-colors"
                        >
                          <Download className="w-3.5 h-3.5" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}

                {/* Valor */}
                {Number(apontamento.valorHora) > 0 && (
                  <div className="text-sm font-medium text-foreground">
                    Valor: {formatCurrency(apontamento.valorHora)}/h ={' '}
                    {formatCurrency(
                      (apontamento.minutosTotais / 60) * Number(apontamento.valorHora)
                    )}
                  </div>
                )}
              </div>

              {/* Ações */}
              {!apontamento.faturado && (
                <div className="flex items-center gap-2">
                  <Button
                    onClick={() => setEditingApontamento(apontamento)}
                    variant="ghost"
                    size="icon"
                    className="text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 hover:text-blue-600"
                    title="Editar"
                  >
                    <Edit />
                  </Button>
                  <Button
                    onClick={() => setShowDeleteConfirm(apontamento.id)}
                    variant="ghost"
                    size="icon"
                    className="text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 hover:text-red-600"
                    title="Excluir"
                  >
                    <Trash2 />
                  </Button>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Totalizações */}
      <div className="border-t border-border pt-6">
        <h3 className="text-sm font-semibold text-foreground mb-4">Totalizações</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {Object.entries(totalizacoes).map(([tipo, data]) => (
            <div
              key={tipo}
              className="p-4 rounded-lg border border-border bg-muted/30"
            >
              <p className="text-xs font-medium text-muted-foreground uppercase mb-1">{tipo}</p>
              <p className="text-2xl font-bold text-foreground">
                {data.horas.toFixed(2)}h
              </p>
              <p className="text-sm text-muted-foreground mt-1">{formatCurrency(data.valor)}</p>
            </div>
          ))}
        </div>

        {valorTotalFaturavel > 0 && (
          <div className="mt-4 border border-brand/40 p-4 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-foreground">Valor Total Faturável</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Excluindo horas de projeto (escopo fechado)
                </p>
              </div>
              <p className="text-3xl font-bold text-brand">{formatCurrency(valorTotalFaturavel)}</p>
            </div>
          </div>
        )}
      </div>

      {/* Modal de Edição */}
      {editingApontamento && (
        <ApontamentoForm
          ticketId={editingApontamento.ticketId}
          apontamento={editingApontamento}
          onClose={() => setEditingApontamento(null)}
          onSuccess={() => {
            setEditingApontamento(null);
            onUpdate();
          }}
        />
      )}

      {/* Modal de Confirmação de Exclusão */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-card border border-border rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-foreground mb-4">
              Confirmar Exclusão
            </h3>
            <p className="text-muted-foreground mb-6">
              Tem certeza que deseja excluir este apontamento? Esta ação não pode ser
              desfeita.
            </p>
            <div className="flex items-center justify-end gap-4">
              <Button
                onClick={() => setShowDeleteConfirm(null)}
                variant="outline"
                disabled={!!deletingId}
              >
                Cancelar
              </Button>
              <Button
                onClick={() => handleDelete(showDeleteConfirm)}
                disabled={!!deletingId}
                variant="destructive"
              >
                {deletingId === showDeleteConfirm && (
                  <Loader2 className="animate-spin" />
                )}
                Excluir
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
